import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFeatures } from '../hooks/useFeatures';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Clock, 
  Warning,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  SpinnerGap,
  Trophy,
  Target
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { showNewBadges } from '../components/BadgeToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MockExamPage = () => {
  const navigate = useNavigate();
  const { hasFeature, pricing, loading: featuresLoading } = useFeatures();
  const [stage, setStage] = useState('intro'); // intro, exam, results
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(57 * 60); // 57 minutes
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [passmark, setPassmark] = useState(43);

  const startExam = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/questions/mock-exam`, { withCredentials: true });
      setQuestions(data.questions);
      setPassmark(data.pass_mark);
      setTimeLeft(data.time_limit);
      setStage('exam');
      setAnswers({});
      setCurrentIndex(0);
    } catch (error) {
      console.error('Failed to start exam:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitExam = useCallback(async () => {
    setLoading(true);
    try {
      const answerArray = Object.entries(answers).map(([questionId, selectedAnswer]) => ({
        question_id: questionId,
        selected_answer: selectedAnswer
      }));

      const { data } = await axios.post(`${API}/questions/mock-exam/submit`, {
        answers: answerArray,
        time_taken: (57 * 60) - timeLeft
      }, { withCredentials: true });

      setResults(data);
      setStage('results');
      if (data.new_badges?.length) {
        showNewBadges(data.new_badges);
      }
    } catch (error) {
      console.error('Failed to submit exam:', error);
    } finally {
      setLoading(false);
    }
  }, [answers, timeLeft]);

  // Timer
  useEffect(() => {
    if (stage !== 'exam') return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [stage, submitExam]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const selectAnswer = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const goToQuestion = (index) => {
    setCurrentIndex(index);
  };

  const answeredCount = Object.keys(answers).length;
  const progressPercent = (answeredCount / questions.length) * 100;

  // Feature gate check
  if (!featuresLoading && !hasFeature('mock_exam')) {
    return <UpgradePrompt feature="mock_exam" requiredTier="standard" pricing={pricing} />;
  }

  // Intro Screen
  if (stage === 'intro') {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="mock-exam-intro">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-6">
              <Target weight="fill" className="w-10 h-10 text-primary" />
            </div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-4">
              Mock Theory Test
            </h1>
            <p className="text-muted-foreground">
              Simulate the real DVSA theory test experience
            </p>
          </div>

          <div className="bg-card border border-border rounded-sm p-6 mb-8">
            <h2 className="font-heading font-bold text-lg mb-4">Test Information</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                <span><strong>50 questions</strong> - Multiple choice</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-accent flex-shrink-0" />
                <span><strong>57 minutes</strong> - Time limit</span>
              </li>
              <li className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-primary flex-shrink-0" />
                <span><strong>43 correct</strong> - Pass mark (86%)</span>
              </li>
            </ul>
          </div>

          <div className="bg-accent/10 border border-accent/30 rounded-sm p-4 mb-8">
            <div className="flex items-start gap-3">
              <Warning weight="fill" className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold mb-1">Important</p>
                <p className="text-muted-foreground">
                  Once you start, the timer cannot be paused. Make sure you have 57 minutes available.
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={startExam}
            disabled={loading}
            className="w-full rounded-sm h-12 font-bold"
            data-testid="start-exam-button"
          >
            {loading ? (
              <SpinnerGap className="w-5 h-5 animate-spin" />
            ) : (
              'Start Mock Exam'
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (stage === 'results') {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="mock-exam-results">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className={cn(
              "w-24 h-24 rounded-sm flex items-center justify-center mx-auto mb-6",
              results.passed ? "bg-success/20" : "bg-destructive/20"
            )}>
              {results.passed ? (
                <Trophy weight="fill" className="w-12 h-12 text-success" />
              ) : (
                <XCircle weight="fill" className="w-12 h-12 text-destructive" />
              )}
            </div>
            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mb-2">
              {results.passed ? 'Congratulations!' : 'Keep Practicing'}
            </h1>
            <p className="text-muted-foreground">
              {results.passed 
                ? 'You passed the mock theory test!' 
                : "You didn't pass this time, but keep practicing!"}
            </p>
          </div>

          {/* Score Card */}
          <div className="bg-card border border-border rounded-sm p-6 mb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-heading font-black text-primary">{results.score}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Correct</p>
              </div>
              <div>
                <p className="text-3xl font-heading font-black">{results.total}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              </div>
              <div>
                <p className="text-3xl font-heading font-black">{results.percentage}%</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Score</p>
              </div>
              <div>
                <p className="text-3xl font-heading font-black">{formatTime(results.time_taken)}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Time</p>
              </div>
            </div>
          </div>

          {/* Question Review */}
          <div className="mb-8">
            <h2 className="font-heading font-bold text-lg mb-4">Question Review</h2>
            <div className="grid grid-cols-10 gap-2">
              {results.results.map((r, index) => (
                <div
                  key={r.question_id}
                  className={cn(
                    "w-full aspect-square rounded-sm flex items-center justify-center text-xs font-bold",
                    r.correct 
                      ? "bg-success/20 text-success" 
                      : "bg-destructive/20 text-destructive"
                  )}
                >
                  {index + 1}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="flex-1 rounded-sm"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => setStage('intro')}
              className="flex-1 rounded-sm"
              data-testid="retake-exam"
            >
              Retake Exam
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Exam Screen
  const currentQuestion = questions[currentIndex];
  const timeWarning = timeLeft < 300; // 5 minutes
  const timeDanger = timeLeft < 60; // 1 minute

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="mock-exam-active">
      {/* Header */}
      <div className="bg-card border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <span className="text-xs text-muted-foreground">
              {answeredCount} answered
            </span>
          </div>
          <div className={cn(
            "flex items-center gap-2 font-mono font-bold",
            timeDanger && "timer-danger",
            timeWarning && !timeDanger && "timer-warning"
          )}>
            <Clock weight="bold" className="w-5 h-5" />
            {formatTime(timeLeft)}
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-3">
          <Progress value={progressPercent} className="h-1 rounded-none" />
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 max-w-4xl mx-auto w-full p-4 md:p-8">
        <div className="animate-slide-in">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-4">
            {currentQuestion.category}
          </p>
          <h2 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-8">
            {currentQuestion.question_text}
          </h2>

          {/* Question Image */}
          {currentQuestion.image_url && (
            <div className="mb-8 flex justify-center">
              <img
                src={currentQuestion.image_url}
                alt="Question illustration"
                className="max-w-xs w-full rounded-sm border border-border bg-muted p-2"
                data-testid="question-image"
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQuestion.options.map((option, index) => {
              const isSelected = answers[currentQuestion.id] === index;

              return (
                <button
                  key={index}
                  onClick={() => selectAnswer(currentQuestion.id, index)}
                  className={cn(
                    "quiz-option w-full text-left",
                    isSelected && "selected"
                  )}
                  data-testid={`exam-option-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 border",
                      isSelected ? "bg-primary border-primary text-primary-foreground" : "border-border"
                    )}>
                      <span className="font-bold text-sm">{String.fromCharCode(65 + index)}</span>
                    </div>
                    <span className="pt-1">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-card border-t border-border p-4 sticky bottom-0">
        <div className="max-w-4xl mx-auto">
          {/* Question Navigator */}
          <div className="flex flex-wrap gap-1 mb-4 justify-center">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => goToQuestion(index)}
                className={cn(
                  "w-8 h-8 text-xs font-bold rounded-sm transition-colors",
                  currentIndex === index && "ring-2 ring-primary",
                  answers[q.id] !== undefined 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              variant="outline"
              className="rounded-sm"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            {currentIndex < questions.length - 1 ? (
              <Button
                onClick={() => setCurrentIndex(currentIndex + 1)}
                className="flex-1 rounded-sm"
              >
                Next
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submitExam}
                disabled={loading}
                className="flex-1 rounded-sm bg-success hover:bg-success/90"
                data-testid="submit-exam"
              >
                {loading ? (
                  <SpinnerGap className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Submit Exam
                    <CheckCircle className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
